import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Download, KeyRound, RefreshCcw, Trash2, Package } from "lucide-react";
import {
  api,
  type Site,
  type WpConnectorStatus,
  type WpConnectorEnableResponse,
  type WpConnectorTestResponse,
  type WpPluginManifest,
  type TrackerStatus,
  type TrackerEnableResponse,
} from "../api";
import { useConfirm } from "./Modal";

interface Props {
  site: Site;
  onClose?: () => void;
}

export function SiteIntegrations({ site }: Props) {
  // A single confirm modal instance shared by both cards — the useConfirm
  // hook keeps state at the parent so nested cards don't each render one.
  const [confirm, confirmNode] = useConfirm();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
      <WpConnectorCard siteId={site.id} homepageUrl={site.homepage_url} confirm={confirm} />
      <TrackerCard siteId={site.id} confirm={confirm} />
      {confirmNode}
    </div>
  );
}

type ConfirmFn = (opts: { title: string; message: React.ReactNode; confirmLabel?: string; tone?: "primary" | "danger" | "warning" }) => Promise<boolean>;

/* ─────────────────────────── WordPress connector ─────────────────────────── */

function WpConnectorCard({ siteId, homepageUrl, confirm }: { siteId: string; homepageUrl: string; confirm: ConfirmFn }) {
  const [state, setState] = useState<WpConnectorStatus | null>(null);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [test, setTest] = useState<WpConnectorTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [manifest, setManifest] = useState<WpPluginManifest | null>(null);

  const refresh = useCallback(async () => {
    try { setState(await api.wpConnectorStatus(siteId)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [siteId]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    // Best-effort: if the manifest endpoint is unreachable we just hide the
    // version chip — the download button still works with a static fallback.
    api.wpPluginManifest().then(setManifest).catch(() => setManifest(null));
  }, []);

  async function enable() {
    setBusy(true); setError(null); setTest(null);
    try {
      const res: WpConnectorEnableResponse = await api.wpConnectorEnable(siteId);
      setFreshToken(res.token);
      setState(res);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function rotate() {
    const ok = await confirm({
      title: "Rotate the bearer token?",
      message: "The old token stops working immediately. You'll need to paste the new one into the WP plugin's settings.",
      confirmLabel: "Rotate token",
      tone: "warning",
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      const res: WpConnectorEnableResponse = await api.wpConnectorRotate(siteId);
      setFreshToken(res.token);
      setState(res);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function disable() {
    const ok = await confirm({
      title: "Disable the WP connector?",
      message: "The plugin will stop syncing PDFs from this site. Existing conversions are untouched.",
      confirmLabel: "Disable connector",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try { await api.wpConnectorDisable(siteId); await refresh(); setFreshToken(null); setTest(null); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function testConn() {
    setBusy(true); setError(null);
    try { setTest(await api.wpConnectorTest(siteId)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function sync() {
    setBusy(true); setError(null);
    try { await api.wpConnectorSync(siteId); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-800">WordPress connector</h3>
        </div>
        {state?.enabled && <span className="pill-ready text-xs">enabled</span>}
      </div>
      <div className="card-body space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          Install the SmartComply plugin on <span className="font-mono">{new URL(homepageUrl).hostname}</span> and paste the token below. SmartComply will then enumerate every PDF directly, no crawling needed.
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          {manifest ? (
            <a
              href={api.wpPluginZipUrl(manifest.version)}
              className="btn-primary !text-xs"
              download
            >
              <Download className="h-3.5 w-3.5" /> Download plugin (v{manifest.version})
            </a>
          ) : (
            <a
              href={api.wpPluginManifestUrl()}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost !text-xs"
            >
              <Download className="h-3.5 w-3.5" /> Download plugin
            </a>
          )}
          <a
            href={api.wpPluginManifestUrl()}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost !text-xs"
          >
            View manifest
          </a>
        </div>

        <div className="text-xs text-slate-600">
          <div><strong>Endpoint:</strong> {state?.endpoint ?? "—"}</div>
          <div><strong>Last sync:</strong> {state?.last_sync_at ? new Date(state.last_sync_at).toLocaleString() : "—"}</div>
          <div><strong>Plugin version:</strong> {state?.plugin_version ?? "—"}</div>
          <div>
            <strong>Real-time webhook:</strong>{" "}
            <WebhookHealth lastAt={state?.last_webhook_at ?? null} />
          </div>
        </div>

        {state?.enabled && state?.webhook_url && (
          <TokenReveal
            token={state.webhook_url}
            label="Real-time webhook URL — paste into Settings → SmartComply → Real-time webhook URL on the WP site"
            multiline
          />
        )}

        {state?.enabled && (
          <CadenceControl
            siteId={siteId}
            hours={state?.crawl_cadence_hours ?? 24}
            onChange={(next) => setState((s) => (s ? { ...s, crawl_cadence_hours: next } : s))}
          />
        )}

        {freshToken && (
          <TokenReveal token={freshToken} label="Bearer token — paste into Settings → SmartComply on the WP site" />
        )}

        {test && (
          <div className={`text-xs rounded px-2 py-1.5 ring-1 ring-inset ${test.ok ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-rose-50 text-rose-700 ring-rose-200"}`}>
            {test.ok
              ? <>Plugin online — v{test.version}, {test.pdf_count} PDFs known.</>
              : <>Test failed: {test.error}</>}
          </div>
        )}
        {error && <div className="text-xs text-rose-700">{error}</div>}

        <div className="flex flex-wrap gap-2">
          {!state?.enabled ? (
            <button onClick={enable} disabled={busy} className="btn-primary">
              <KeyRound className="h-3.5 w-3.5" /> Enable + generate token
            </button>
          ) : (
            <>
              <button onClick={testConn} disabled={busy} className="btn-secondary">Test connection</button>
              <button onClick={sync} disabled={busy} className="btn-secondary">
                <RefreshCcw className="h-3.5 w-3.5" /> Sync now
              </button>
              <button onClick={rotate} disabled={busy} className="btn-ghost">Rotate token</button>
              <button onClick={disable} disabled={busy} className="btn-ghost text-rose-600">
                <Trash2 className="h-3.5 w-3.5" /> Disable
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────── Tracker ───────────────────────────────── */

function TrackerCard({ siteId, confirm }: { siteId: string; confirm: ConfirmFn }) {
  const [state, setState] = useState<TrackerStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { setState(await api.trackerStatus(siteId)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [siteId]);
  useEffect(() => { refresh(); }, [refresh]);

  async function enable() {
    setBusy(true); setError(null);
    try { const res: TrackerEnableResponse = await api.trackerEnable(siteId); setState(res); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function rotate() {
    const ok = await confirm({
      title: "Rotate the tracker token?",
      message: "The current snippet on your site stops working immediately — you'll need to swap in the new one.",
      confirmLabel: "Rotate token",
      tone: "warning",
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try { const res: TrackerEnableResponse = await api.trackerRotate(siteId); setState(res); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function disable() {
    const ok = await confirm({
      title: "Disable the visitor tracker?",
      message: "New page visits stop being captured. Existing discovered PDFs remain.",
      confirmLabel: "Disable tracker",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try { await api.trackerDisable(siteId); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-800">Visitor tracker</h3>
        </div>
        {state?.enabled && <span className="pill-ready text-xs">enabled</span>}
      </div>
      <div className="card-body space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          A tiny script that captures PDF links as real visitors open them — covers pages the crawler and WP plugin might miss.
        </p>
        <div className="text-xs text-slate-600">
          <strong>Last event:</strong> {state?.last_event_at ? new Date(state.last_event_at).toLocaleString() : "—"}
        </div>
        {state?.enabled && state.snippet && (
          <TokenReveal token={state.snippet} label="Add this script tag to your site" multiline />
        )}
        {error && <div className="text-xs text-rose-700">{error}</div>}
        <div className="flex flex-wrap gap-2">
          {!state?.enabled ? (
            <button onClick={enable} disabled={busy} className="btn-primary">Enable tracker</button>
          ) : (
            <>
              <button onClick={rotate} disabled={busy} className="btn-ghost">Rotate token</button>
              <button onClick={disable} disabled={busy} className="btn-ghost text-rose-600">
                <Trash2 className="h-3.5 w-3.5" /> Disable
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── shared bits ─────────────────────────── */

function WebhookHealth({ lastAt }: { lastAt: string | null }) {
  if (!lastAt) {
    return <span className="text-slate-500">never — falling back to scheduled sync</span>;
  }
  const ms = Date.now() - new Date(lastAt).getTime();
  const minutes = Math.round(ms / 60_000);
  const label = minutes < 1
    ? "just now"
    : minutes < 60
      ? `${minutes} min ago`
      : `${Math.round(minutes / 60)} hr ago`;
  const stale = ms > 7 * 24 * 60 * 60 * 1000;
  return (
    <span className={stale ? "text-amber-700" : "text-emerald-700"}>{label}</span>
  );
}

function CadenceControl({
  siteId, hours, onChange,
}: { siteId: string; hours: number; onChange: (n: number) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function pick(next: number) {
    setBusy(true); setErr(null);
    const prev = hours;
    onChange(next);
    try { await api.updateCrawlCadence(siteId, next); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); onChange(prev); }
    finally { setBusy(false); }
  }
  const opts: { label: string; value: number }[] = [
    { label: "Every hour", value: 1 },
    { label: "Every 6 hours", value: 6 },
    { label: "Every 12 hours", value: 12 },
    { label: "Every day", value: 24 },
    { label: "Every week", value: 168 },
  ];
  return (
    <div className="text-xs text-slate-600">
      <label className="block font-medium mb-1">Auto-crawl cadence</label>
      <select
        className="input !py-1 !text-xs"
        value={hours}
        onChange={(e) => pick(Number(e.target.value))}
        disabled={busy}
      >
        {opts.some((o) => o.value === hours) ? null : (
          <option value={hours}>{`Every ${hours} hours (custom)`}</option>
        )}
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <p className="text-[11px] text-slate-500 mt-1">
        The webhook still fires on every upload. This is the safety-net scheduled sync.
      </p>
      {err && <p className="text-[11px] text-rose-700">{err}</p>}
    </div>
  );
}

/* ─────────────────────────── shared: token / snippet reveal ─────────────────────────── */

function TokenReveal({ token, label, multiline = false }: { token: string; label: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  }
  return (
    <div className="border border-slate-200 rounded p-2 bg-slate-50 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="text-slate-600">{label}</span>
        <button onClick={copy} className="btn-ghost !px-1.5 !py-0.5 text-xs" aria-label="Copy">
          {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
        </button>
      </div>
      <pre className={`font-mono text-[11px] text-slate-800 ${multiline ? "whitespace-pre-wrap break-all" : "truncate"}`}>{token}</pre>
    </div>
  );
}
